'use client';

import { Flex, Grid, Switch, Typography } from '@coolblue-development/freddy';
import { MiniProductPage } from '@coolblue-development/next-mini-product-page/client';
import { SalesPrice, SalesPriceCurrent } from '@coolblue-development/next-sales-price/client';
import { CROSS_SELL_CHARGER_FIELD_NAME } from '@features/product/components/OrderBlock/UsbCCharger/constants';
import type { Accessory } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { CSCAddChargerToOrderFromPDP } from '../../../../../../splitTests';
import type { CrossSellChargerVariation } from '../types';
import styles from './Charger.styles';
import Icon from './Icon';

/*
 * Next tries to preserve form state on navigation, which in this case is a footgun.
 * By forcing the input's checked property to false on each render, we ensure that
 * the visual state of the Switch matches the component state.
 */
function forceInputState(element: HTMLInputElement | null) {
  if (element) {
    element.checked = false;
  }
}

export default function Charger({
  charger,
  variation
}: {
  charger: Accessory;
  variation: CrossSellChargerVariation;
}) {
  const { t } = useTranslation('webshop.orderBlock.crossSellCharger');
  const [toggled, setToggled] = useState(() => false);

  const { id, shortName, prices } = charger.product;
  const { container, toggleSwitch, getAccessorySummary } = styles;

  const statusId = `charger-status-${id}`;
  const switchId = `charger-switch-${id}`;

  function flipSwitch() {
    setToggled(toggleStatus => !toggleStatus);
  }

  return (
    <Grid templateColumns='auto 1fr' alignItems='center' columnGap='1rem' sx={container}>
      <Switch
        aria-describedby={statusId}
        aria-label={t('switchLabel', { productName: shortName })}
        checked={toggled}
        id={switchId}
        name={CROSS_SELL_CHARGER_FIELD_NAME}
        onChange={flipSwitch}
        ref={forceInputState}
        sx={toggleSwitch}
        value={id}
      />

      <Flex alignItems='center' gap='.6rem' onClick={flipSwitch} sx={{ cursor: 'pointer' }}>
        <Icon toggled={toggled} aria-hidden='true' />

        <Typography component='span' id={statusId} aria-live='polite'>
          {t(toggled ? 'added' : 'notAdded')}
        </Typography>
      </Flex>

      <Flex
        alignItems='flex-start'
        gap='200'
        pl='2.2rem'
        sx={getAccessorySummary(toggled)}
        role='region'
        aria-label={t('productDetails')}
        {...(!toggled && { inert: true })}
        aria-hidden={!toggled}
      >
        {variation === CSCAddChargerToOrderFromPDP.VARIATION_B && (
          <MiniProductPage productId={id}>{shortName}</MiniProductPage>
        )}
        {variation === CSCAddChargerToOrderFromPDP.VARIATION_C && (
          <Typography color='interactiveSubtle'>{shortName}</Typography>
        )}

        <SalesPrice
          currentPrice={<SalesPriceCurrent price={prices.salesPrice.includingVat} />}
          size='small'
        />
      </Flex>
    </Grid>
  );
}
